import {
  convertCssTimeToMilliseconds,
  getCssPropertyValue,
  trapFocus
} from '../../../ts/helper';
import { lazyLoading } from '../../../ts/lazyLoading';
import { atomIcon } from '../atomIcon';

interface LightboxLabels {
  /** Label on lightbox. Default is `"Lightbox"` */
  lightbox?: string;

  /**
   * Used in the counter: `"{instance} 1 of 3"`. Default is `"Image"`.
   * @example
   *   "Bild"
   */
  instance?: string;

  /**
   * Used in the counter: `"Item 1 {instanceOf} 3"`. Default is `"of"`.
   * @example
   *   "von"
   */
  instanceOf?: string;
}

interface LightboxCounter {
  index: number;
  total: number;
  caption?: HTMLElement;
}

interface LightboxOptions {
  /** Element that will display {@link content} in a lightbox on click */
  trigger: HTMLAnchorElement | HTMLButtonElement;

  /** Content that will be visible in the lightbox */
  content: HTMLElement;

  /** Labels */
  labels?: LightboxLabels;

  /** Counter ("Item X of Y") */
  counter?: LightboxCounter;
}

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="atom-lightbox"]';

/**
 * Create a lightbox for any content
 */
export class Lightbox {
  /** Options */
  params: LightboxOptions;

  /** Lightbox instance */
  component: HTMLDivElement;

  /** Close button will destroy the {@link component} */
  closeButton: HTMLButtonElement;

  /** This will contain the {@link LightboxOptions.content} */
  contentSlot: HTMLDivElement;

  /** Counter that reads:
   * `"{@link LightboxLabels.instance} {@link LightboxCounter.index} {@link LightboxLabels.instanceOf} {@link LightboxCounter.total}"`
   * `"{@link counterLabelInstance} {@link counterIndex} {@link counterLabelInstanceOf} {@link counterTotal}"`
   * like `"Image 2 of 3"`. */
  counter: HTMLDivElement;
  counterTotal: HTMLSpanElement;
  counterIndex: HTMLSpanElement;
  counterLabelInstance: HTMLSpanElement;
  counterLabelInstanceOf: HTMLSpanElement;
  imageCaption: HTMLDivElement;
  removeTrapFocus: () => void;
  contentWrapper: HTMLElement;

  constructor(options: LightboxOptions) {
    // Create component from blueprint
    this.component = <HTMLDivElement>(
      document.querySelector(selector)?.cloneNode(true)
    );
    if (!this.component) return;

    // read options
    this.params = options;

    // find close button
    this.closeButton = <HTMLButtonElement>(
      this.component.querySelector('.close')
    );

    // find content wrapper which we need to detect a click outside of it to close the lightbox
    this.contentWrapper = <HTMLElement>(
      this.component.querySelector('.contentWrapper')
    );

    // find counter
    this.counter = <HTMLDivElement>this.component.querySelector('.counter');
    this.imageCaption = <HTMLDivElement>(
      this.component.querySelector('.img-caption')
    );
    if (this.counter) {
      this.counterTotal = <HTMLSpanElement>this.counter.querySelector('.total');
      this.counterIndex = <HTMLSpanElement>this.counter.querySelector('.index');
      this.counterLabelInstance = <HTMLSpanElement>(
        this.counter.querySelector('.labelInstance')
      );
      this.counterLabelInstanceOf = <HTMLSpanElement>(
        this.counter.querySelector('.labelInstanceOf')
      );
    }

    // find content container
    this.contentSlot = <HTMLDivElement>this.component.querySelector('.content');

    // Init
    this.create();
  }

  private create() {
    // Set params
    this.params.trigger.setAttribute('aria-expanded', 'false');

    // Add Event Listeners
    this.closeButton.addEventListener('click', this.hide.bind(this));
    this.params.trigger.addEventListener('click', this.show.bind(this));

    // Modify content
    const content = <HTMLElement>this.params.content.cloneNode(true);
    content.inert = false;
    content.hidden = false;
    this.contentSlot.append(content);

    // Modify labels
    if (this.params.labels) {
      if (this.params.labels.lightbox)
        this.component.setAttribute('aria-label', this.params.labels.lightbox);
    }

    if (this.params.counter) {
      this.component.setAttribute(
        'aria-label',
        `${this.component.getAttribute('aria-label')}, ${this.params.labels.instance} ${this.params.counter.index.toString()} ${this.params.labels.instanceOf} ${this.params.counter.total.toString()}`
      );
    }

    // Prepare for visibility change
    this.component.removeAttribute('hidden'); // component is still inert
  }

  private show(e?: MouseEvent) {
    if (e) e.preventDefault();

    // Handle state on trigger
    this.params.trigger.setAttribute('aria-expanded', 'true');

    // Handle state on counter
    if (this.params.counter) {
      this.counter.inert = false;
      if (this.params.counter.total)
        this.counterTotal.innerText = this.params.counter.total.toString();
      if (this.params.counter.index)
        this.counterIndex.innerText = this.params.counter.index.toString();
      if (this.params.labels.instance)
        this.counterLabelInstance.innerText = this.params.labels.instance;
      if (this.params.labels.instanceOf)
        this.counterLabelInstanceOf.innerText = this.params.labels.instanceOf;
      if (this.imageCaption) {
        const caption = this.params.counter.caption;
        if (caption?.textContent?.trim()) {
          this.imageCaption.inert = false;
          this.imageCaption.textContent = caption.textContent.trim();
        } else {
          this.imageCaption.textContent = '';
        }
      }
    }

    // Listen for events
    document.addEventListener('keydown', this.onKeydown.bind(this));

    // Add blueprint to DOM
    document.body.append(this.component);

    // Handle state on component: show
    this.component.inert = false;

    // Run Lazyload again
    lazyLoading();
    atomIcon();

    setTimeout(() => {
      // Handle tabindex and focus
      this.component.tabIndex = -1;
      this.component.focus();

      // Trap focus
      this.removeTrapFocus = trapFocus(this.component);

      // Listen for click outside
      this.component.addEventListener('click', this.onClick.bind(this), true);
    }, 100);
  }

  public onClick(e: MouseEvent) {
    const target = <HTMLElement>e.target;

    if (!this.contentWrapper.contains(target)) {
      this.hide();
    }
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      this.hide();
    }
  }

  private hide() {
    // Handle state on trigger
    this.params.trigger.setAttribute('aria-expanded', 'false');

    // Handle state on component: hide
    this.component.inert = true;

    // Remove: listen for click outside
    this.component.removeEventListener('click', this.onClick.bind(this), true);

    // Remove focus trap
    this.removeTrapFocus();

    setTimeout(() => {
      // Focus on trigger
      this.params.trigger.focus();
    }, 100);

    setTimeout(
      () => {
        this.destroy();
      },
      convertCssTimeToMilliseconds(
        getCssPropertyValue('--transition-duration')
      ) + 10
    );
  }

  /**
   * Destroys the {@link component}
   */
  private destroy() {
    this.component.remove();
  }
}
